// Frontend/src/components/admin/EventsManager.jsx
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCampuses } from '../../contexts/CampusContext';
import { useEvents } from '../../contexts/EventsContext';
import { useMinistries } from '../../contexts/MinistryContext';
import EventService from '../../services/eventService';
import EventForm from './forms/EventForm';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import DataTable from './ui/DataTable';
import EventTypeBadge from './ui/EventTypeBadge';
import Modal from './ui/Modal';

const EventsManager = () => {
  const { events, loading, error, setEvents } = useEvents();
  const { campuses } = useCampuses();
  const { ministries } = useMinistries(); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // New state for modern features
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');

  const parseDate = (dateValue) => {
    if (!dateValue) return new Date();
    
    // Handle Firestore Timestamp format
    if (dateValue._seconds && typeof dateValue._seconds === 'number') {
      const milliseconds = dateValue._seconds * 1000 + Math.floor(dateValue._nanoseconds / 1000000);
      return new Date(milliseconds);
    }
    
    // Handle ISO string or Date object
    const parsed = new Date(dateValue);
    if (isNaN(parsed.getTime())) {
      console.warn('Invalid date value:', dateValue, 'Using current date instead');
      return new Date();
    }
    return parsed;
  };

  const formatDateRange = (startDate, endDate, isSpanningEvent) => {
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    if (!isSpanningEvent) return start.toLocaleDateString();
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  // Filtered and sorted events
  const filteredAndSortedEvents = useMemo(() => {
  let filtered = events.filter(event => {
    // Add safety checks for undefined/null values
    const title = event.title || '';
    const location = event.location || '';
    const description = event.description || '';
    
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || event.eventType === filterType;
    const matchesStatus = filterStatus === 'all' || event.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });


    // Sort events
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'startDate':
          aValue = parseDate(a.startDate).getTime();
          bValue = parseDate(b.startDate).getTime();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'location':
          aValue = a.location.toLowerCase();
          bValue = b.location.toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [events, searchTerm, filterType, filterStatus, sortBy, sortOrder]);

  const eventTypes = [...new Set(events.map(e => e.eventType))];
  const eventStatuses = [...new Set(events.map(e => e.status))];

  const columns = [
    {
      field: 'poster',
      header: 'Poster',
      width: '100px',
      render: (row) => (
        <div className="flex justify-center">
          <div className="relative group">
            <img 
              className="h-12 w-12 rounded-xl object-cover border-2 border-gray-200 shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg" 
              src={row.imageUrl} 
              alt={row.title}
              onError={(e) => {
                e.target.src = '/api/placeholder/48/48';
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-xl transition-all duration-300"></div>
          </div>
        </div>
      )
    },
    { 
      field: 'title', 
      header: 'Title', 
      width: '25%',
      render: (row) => (
        <div className="font-semibold text-gray-900 hover:text-blue-600 transition-colors duration-200">
          {row.title}
        </div>
      )
    },
    { 
      field: 'date', 
      header: 'Date', 
      type: 'date', 
      width: '15%',
      render: (row) => {
        const date = parseDate(row.date);
        const isUpcoming = date > new Date();
        return (
          <div className={`font-medium ${isUpcoming ? 'text-green-600' : 'text-gray-500'}`}>
            {formatDateRange(row.startDate, row.endDate, row.isSpanningEvent)}
          </div>
        );
      }
    },
    { 
      field: 'location', 
      header: 'Location', 
      width: '20%',
      render: (row) => (
        <div className="text-gray-700 font-medium">
          {row.location}
        </div>
      )
    },
    { 
      field: 'eventType', 
      header: 'Type', 
      width: '15%',
      render: (row) => <EventTypeBadge type={row.eventType} />
    },
    {
      field: 'actions',
      header: 'Actions',
      width: '25%',
      render: (row) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => handleEdit(row)}
            className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-all duration-200 hover:scale-105"
          >
            Edit
          </button>
          <button 
            onClick={() => handleDeleteClick(row)}
            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-all duration-200 hover:scale-105"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const handleDeleteClick = (event) => {
    setCurrentEvent(event);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      setProcessing(true);
      await EventService.deleteEvent(currentEvent.id);
      setEvents(events.filter(item => item.id !== currentEvent.id));
      toast.success('Event deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete event: ${error.message}`);
    } finally {
      setProcessing(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleEdit = (event) => {
    // Parse dates properly before passing to the form
    const eventWithParsedDates = {
      ...event,
      startDate: parseDate(event.startDate),
      endDate: parseDate(event.endDate),  
      registrationDeadline: event.registrationDeadline 
        ? parseDate(event.registrationDeadline) 
        : null
    };
    setCurrentEvent(eventWithParsedDates);
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    try {
      setProcessing(true);
      let updatedEvents;
      
      if (currentEvent) {
        const updated = await EventService.updateEvent(currentEvent.id, formData);
        updatedEvents = events.map(e => 
          e.id === currentEvent.id ? updated : e
        );
        toast.success('Event updated successfully');
      } else {
        const created = await EventService.createEvent(formData);
        console.log('Created event:', created);
        updatedEvents = [...events, created];
        setEvents(updatedEvents);
        toast.success('Event created successfully');
      }
      
      setEvents(updatedEvents);
      setIsModalOpen(false);
      setCurrentEvent(null);
    } catch (error) {
      toast.error(`Failed to save event: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const campusOptions = campuses?.map(m => ({
    value: m.name.toLowerCase(),
    label: m.name
  })) || [];

  const ministryOptions = ministries?.map(m => ({
    value: m.name.toLowerCase(),
    label: m.name
  })) || [];

  // Grid view component
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {filteredAndSortedEvents.map((event) => (
        <div key={event.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group hover:scale-[1.02]">
          <div className="relative h-48 bg-gradient-to-br from-blue-500 to-purple-600">
            <img 
              src={event.imageUrl} 
              alt={event.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/api/placeholder/300/200';
              }}
            />
            <div className="absolute top-3 right-3">
              <EventTypeBadge type={event.eventType} />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
          
          <div className="p-5">
            <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
              {event.title}
            </h3>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-gray-600 text-sm">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDateRange(event.startDate, event.endDate, event.isSpanningEvent)}
              </div>
              
              <div className="flex items-center text-gray-600 text-sm">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {event.location}
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button 
                onClick={() => handleEdit(event)}
                className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200"
              >
                Edit
              </button>
              <button 
                onClick={() => handleDeleteClick(event)}
                className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Events Manager</h1>
              <p className="text-gray-600">Manage your church events and activities</p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === 'table' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    viewMode === 'grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
              
              <Button 
                onClick={() => {
                  setCurrentEvent(null);
                  setIsModalOpen(true);
                }}
                variant="primary"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Event
              </Button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                />
              </div>
            </div>

            {/* Event Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
            >
              <option value="all">All Types</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
            >
              <option value="all">All Status</option>
              {eventStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
            >
              <option value="date-asc">Date (Old to New)</option>
              <option value="date-desc">Date (New to Old)</option>
              <option value="title-asc">Title (A-Z)</option>
              <option value="title-desc">Title (Z-A)</option>
              <option value="location-asc">Location (A-Z)</option>
              <option value="location-desc">Location (Z-A)</option>
            </select>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
            <div className="text-sm text-gray-600">
              Showing {filteredAndSortedEvents.length} of {events.length} events
            </div>
            {(searchTerm || filterType !== 'all' || filterStatus !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                  setFilterStatus('all');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-8">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800 font-medium">
                {error.message || 'Failed to load events'}
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {viewMode === 'table' ? (
            <div className="p-6">
              <DataTable
                data={filteredAndSortedEvents}
                columns={columns}
                loading={loading}
                emptyMessage="No events found"
              />
            </div>
          ) : (
            <div className="p-6">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-gray-200 h-48 rounded-2xl mb-4"></div>
                      <div className="space-y-2">
                        <div className="bg-gray-200 h-4 rounded w-3/4"></div>
                        <div className="bg-gray-200 h-3 rounded w-1/2"></div>
                        <div className="bg-gray-200 h-3 rounded w-2/3"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredAndSortedEvents.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-gray-500 text-lg">No events found</p>
                  <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filters</p>
                </div>
              ) : (
                <GridView />
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setCurrentEvent(null);
          }}
          title={currentEvent ? 'Edit Event' : 'Create New Event'}
          size="lg"
        >
          <EventForm
            initialValues={currentEvent || {
              title: '',
              description: '',
              startDate: new Date(),
                endDate: new Date(),
                isSpanningEvent: false,
              location: '',
              eventType: 'service',
              address: '',
              contactEmail: '',
              contactPhone: '',
              isRecurring: false,
              recurrencePattern: '',
              registrationRequired: false,
              registrationDeadline: null,
              cost: 0,
              status: 'upcoming',
              campusId: '',
              ministry_name: '',
              imageUrl: ''
            }}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsModalOpen(false);
              setCurrentEvent(null);
            }}
            isSubmitting={processing}
            campuses={campusOptions}
            ministries={ministryOptions}
          />
        </Modal>

        <ConfirmDialog
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={handleDelete}
          title="Confirm Delete"
          message={`Are you sure you want to delete "${currentEvent?.title}" event?`}
          confirmText="Delete"
          cancelText="Cancel"
          processing={processing}
        />
      </div>
    </div>
  );
};

export default EventsManager;